import React, { useState, useEffect, useCallback } from 'react';
import { searchCustomers } from '../services/customersService';
import { formatTelefonGoster, normalizeTurkishPhone } from '../utils/phoneNormalize';

const musteriAdSoyad = (m) => {
  const ad = (m.adi || m.name || '').trim();
  const soyad = (m.soyadi || m.surname || '').trim();
  return [ad, soyad].filter(Boolean).join(' ') || 'İsimsiz müşteri';
};

const musteriTelefon = (m) => normalizeTurkishPhone(m.telefon || m.phone || '');

/**
 * Santral paneli — müşteri arama ve giden arama sekmesi.
 */
const SantralAramaSekmesi = ({
  isRegistered,
  hasActive,
  hasIncoming,
  isDialing,
  dialCall,
  appendSantralLog,
}) => {
  const [arama, setArama] = useState('');
  const [sonuclar, setSonuclar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [arananTelefon, setArananTelefon] = useState('');

  useEffect(() => {
    const trimmed = arama.trim();
    if (trimmed.length < 3) {
      setSonuclar([]);
      setYukleniyor(false);
      setHata('');
      return undefined;
    }

    setYukleniyor(true);
    setHata('');

    const timer = setTimeout(async () => {
      try {
        const res = await searchCustomers(trimmed, { aktif: true, limit: 8 });
        const list = res?.veri?.musteriler || res?.data?.customers || res?.musteriler || [];
        setSonuclar(Array.isArray(list) ? list : []);
      } catch (err) {
        setSonuclar([]);
        setHata(err?.message || 'Arama yapılamadı');
      } finally {
        setYukleniyor(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [arama]);

  const aramaEngelli = !isRegistered || hasActive || hasIncoming || isDialing;

  const handleAra = useCallback(async (musteri) => {
    const telefon = musteriTelefon(musteri);
    if (telefon.length !== 10) {
      appendSantralLog?.('Müşterinin geçerli telefon numarası yok', { seviye: 'warning' });
      return;
    }

    if (!isRegistered) {
      appendSantralLog?.('Önce çağrı sistemini başlatın', { seviye: 'warning' });
      return;
    }

    if (hasActive || hasIncoming) {
      appendSantralLog?.('Zaten aktif bir çağrı var', { seviye: 'warning' });
      return;
    }

    setArananTelefon(telefon);
    try {
      const sonuc = await dialCall(telefon);
      if (sonuc?.ok === false) {
        appendSantralLog?.(sonuc.mesaj || 'Arama başlatılamadı', { seviye: 'error' });
      } else {
        appendSantralLog?.(`${formatTelefonGoster(telefon)} aranıyor…`, { seviye: 'info' });
      }
    } finally {
      setArananTelefon('');
    }
  }, [appendSantralLog, dialCall, hasActive, hasIncoming, isRegistered]);

  const trimmed = arama.trim();

  return (
    <div className="santral-arama-sekmesi">
      <div className="santral-arama-kutu">
        <i className="fas fa-search santral-arama-kutu__ikon" aria-hidden />
        <input
          type="search"
          className="santral-arama-kutu__input"
          placeholder="Ad, soyad veya telefon (min 3 karakter)"
          value={arama}
          onChange={(e) => setArama(e.target.value.replace(/[<>]/g, ''))}
          maxLength={100}
          autoComplete="off"
          aria-label="Müşteri ara"
        />
        {yukleniyor ? (
          <i className="fas fa-spinner fa-spin santral-arama-kutu__spinner" aria-hidden />
        ) : null}
      </div>

      {!isRegistered ? (
        <p className="santral-arama-hint">
          <i className="fas fa-info-circle" aria-hidden />
          {' '}
          Arama yapmak için çağrı sistemini başlatın.
        </p>
      ) : null}

      {hata ? (
        <p className="santral-arama-hata">
          <i className="fas fa-exclamation-circle" aria-hidden />
          {' '}
          {hata}
        </p>
      ) : null}

      {trimmed.length > 0 && trimmed.length < 3 ? (
        <p className="santral-arama-bos">En az 3 karakter girin</p>
      ) : null}

      {trimmed.length >= 3 && !yukleniyor && !hata && sonuclar.length === 0 ? (
        <p className="santral-arama-bos">Sonuç bulunamadı</p>
      ) : null}

      {sonuclar.length > 0 ? (
        <ul className="santral-arama-listesi">
          {sonuclar.map((m) => {
            const telefon = musteriTelefon(m);
            const telefonGecerli = telefon.length === 10;
            const araniyor = arananTelefon === telefon;
            const disabled = aramaEngelli || !telefonGecerli || araniyor;
            const key = m.id || m._id || `${telefon}-${musteriAdSoyad(m)}`;

            return (
              <li key={key} className="santral-arama-sonuc">
                <div className="santral-arama-sonuc__bilgi">
                  <span className="santral-arama-sonuc__ad">{musteriAdSoyad(m)}</span>
                  <span className="santral-arama-sonuc__tel">
                    {telefonGecerli ? formatTelefonGoster(telefon) : 'Telefon yok'}
                  </span>
                </div>
                <button
                  type="button"
                  className="santral-arama-btn"
                  onClick={() => handleAra(m)}
                  disabled={disabled}
                  title={telefonGecerli ? 'Ara' : 'Telefon numarası yok'}
                  aria-label={telefonGecerli ? `${musteriAdSoyad(m)} ara` : 'Telefon yok'}
                >
                  <i className={`fas fa-${araniyor ? 'spinner fa-spin' : 'phone'}`} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default SantralAramaSekmesi;
